<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useCampanhaMarketingStore } from '@/stores/campanhaMarketing'
import { useNotificationStore } from '@/stores/notification'
import { useCampanhaFilters } from '@/composables/useCampanhaFilters'

const campanhaMarketingStore = useCampanhaMarketingStore()
const notificationStore = useNotificationStore()

// Usar o composable para filtros compartilhados
const {
  diasFiltroOptions,
  estadosOptions,
  phoneRules,
  confirmDialog,
  confirmationMessage,
  pendingFilters,
  pendingCount,
  validateBasicFilters,
  buildFilters,
  clearBasicForm,
} = useCampanhaFilters()

// Canais selecionados para envio
const canaisSelecionados = ref({
  sms: false,
  push: false,
  whatsapp: false
})

const form = ref({
  // Filtros comuns
  data_inicio: '',
  data_fim: '',
  data_ultima_compra_filtro: 'entre', // 'entre', 'maior_que', 'menor_que', 'null'
  cidade: '',
  estados: [],
  dias_vencimento: '',
  dias_vencimento_filtro: '',
  telefone: '',
  nunca_comprou: false,
  quantidade_pedidos: '', // '', '1', '2', '3', '4+'
  
  // Campos da campanha
  nome_campanha: '',
  
  // Específico para Push
  titulo: '',
  
  // Específico para SMS e Push
  mensagem: '',
  
  // Variáveis dinâmicas para SMS
  sms_include_nome: false,
  sms_include_dtultimacompra: false,
  sms_gateway: 'smsdev', // Gateway padrão
  
  // Específico para WhatsApp
  template_code: '',
  include_nome: false,
  include_dtultimacompra: false,
  include_valor: false,
  include_revenda: false,
  include_vencimento: false,
  whatsapp_provider: 'onemsg' // Provider padrão para WhatsApp
})

const loading = computed(() => campanhaMarketingStore.loading)
const success = computed(() => campanhaMarketingStore.success)
const error = computed(() => campanhaMarketingStore.error)
const lastResult = computed(() => campanhaMarketingStore.lastResult)
const templates = computed(() => campanhaMarketingStore.templates)

// Constantes para SMS
const SMS_MAX_LENGTH = 150

// Opções de provider para WhatsApp
const whatsappProviderOptions = [
  { title: '1-Msg (Onemsg)', value: 'onemsg', icon: 'mdi-message-text-outline', description: 'Conexão via API 1msg.io' },
  { title: 'UTalk', value: 'utalk', icon: 'mdi-chat-processing-outline', description: 'Plataforma UTalk' },
  { title: 'NeedConversas', value: 'need', icon: 'mdi-whatsapp', description: 'Integração NeedConversas' },
  { title: 'Mock (Teste)', value: 'mock', icon: 'mdi-flask-outline', description: 'Modo de teste interno' }
]
const smsLength = computed(() => form.value.mensagem.length)
const smsRemaining = computed(() => SMS_MAX_LENGTH - smsLength.value)
const smsWarning = computed(() => smsLength.value > SMS_MAX_LENGTH * 0.8)

// Verifica se pelo menos um canal está selecionado
const algumCanalSelecionado = computed(() => {
  return canaisSelecionados.value.sms || canaisSelecionados.value.push || canaisSelecionados.value.whatsapp
})

// Lista de canais selecionados para exibição
const canaisSelecionadosTexto = computed(() => {
  const canais = []
  if (canaisSelecionados.value.sms) canais.push('SMS')
  if (canaisSelecionados.value.push) canais.push('Push')
  if (canaisSelecionados.value.whatsapp) canais.push('WhatsApp')
  return canais.join(', ')
})

// Preview do template WhatsApp
const getTemplatePreview = computed(() => {
  if (!canaisSelecionados.value.whatsapp) return ''
  
  const template = templates.value.find(t => t.code === form.value.template_code)
  if (!template) return ''
  
  let text = template.text.replace(/\\n/g, '\n')
  
  // 1. Contextual replacements for fixed info (Optional)
  if (form.value.include_valor) {
    text = text.replace(/(Valor:?\s*R\$?\s*)\{\{\d+\}\}/gi, '$1[VALOR]')
  }
  if (form.value.include_revenda) {
    text = text.replace(/(Revenda:?\s*)\{\{\d+\}\}/gi, '$1[NOME_REVENDA]')
  }
  if (form.value.include_vencimento) {
    text = text.replace(/(Vencimento:?\s*)\{\{\d+\}\}/gi, '$1[DATA_VENCIMENTO]')
  }
  
  // 2. Customer Name (Context or fallback index 1)
  if (form.value.include_nome) {
    text = text.replace(/(Olá\b.*?)\{\{\d+\}\}/gi, '$1[NOME_DO_CLIENTE]')
    text = text.replace(/\{\{1\}\}/g, '[NOME_DO_CLIENTE]')
  }
  
  // 3. Last Purchase Date (Context or fallback index 2)
  if (form.value.include_dtultimacompra) {
    text = text.replace(/(mês\s+de\s+)\{\{\d+\}\}/gi, '$1[DATA_ÚLTIMA_COMPRA]')
    // Fallback only if {{2}} still exists (wasn't picked up by Valor/Revenda)
    text = text.replace(/\{\{2\}\}/g, '[DATA_ÚLTIMA_COMPRA]')
  }
  
  return text
})

const enviarCampanha = async () => {
  campanhaMarketingStore.clearResult()
  
  // Validar se pelo menos um canal foi selecionado
  if (!algumCanalSelecionado.value) {
    notificationStore.showNotification('Selecione pelo menos um canal de envio (SMS, Push ou WhatsApp).', 'warning')
    return
  }
  
  // Validações básicas
  if (!form.value.nome_campanha) {
    notificationStore.showNotification('O campo nome da campanha é obrigatório.', 'warning')
    return
  }
  
  // Validações específicas por canal
  if (canaisSelecionados.value.push) {
    if (!form.value.titulo) {
      notificationStore.showNotification('O campo título é obrigatório para Push.', 'warning')
      return
    }
    if (!form.value.mensagem) {
      notificationStore.showNotification('O campo mensagem é obrigatório para Push.', 'warning')
      return
    }
  }
  
  if (canaisSelecionados.value.sms) {
    if (!form.value.mensagem) {
      notificationStore.showNotification('O campo mensagem é obrigatório para SMS.', 'warning')
      return
    }
    if (form.value.mensagem.length > SMS_MAX_LENGTH) {
      notificationStore.showNotification(`A mensagem não pode ultrapassar ${SMS_MAX_LENGTH} caracteres.`, 'warning')
      return
    }
  }
  
  if (canaisSelecionados.value.whatsapp) {
    if (!form.value.template_code) {
      notificationStore.showNotification('Selecione um template de mensagem para WhatsApp.', 'warning')
      return
    }
  }

  // Validar filtros básicos
  if (!validateBasicFilters(form.value)) {
    return
  }

  // Construir filtros
  const filters = buildFilters(form.value)
  
  // Mapear filtro de data de última compra para compatibilidade com todos os canais
  if (form.value.data_ultima_compra_filtro === 'null') {
    // Para Push/WhatsApp: usar nunca_comprou
    filters.nunca_comprou = 1
    // Para SMS: usar data_ultima_compra_filtro
    filters.data_ultima_compra_filtro = 'null'
  } else if (form.value.data_ultima_compra_filtro) {
    // Para SMS: enviar o filtro específico
    filters.data_ultima_compra_filtro = form.value.data_ultima_compra_filtro
    
    // Para Push/WhatsApp: garantir que data_inicio e data_fim sejam usados corretamente
    // (já estão em buildFilters, mas limpar se não aplicável ao operador)
    if (form.value.data_ultima_compra_filtro === 'maior_que') {
      // Remove data_fim para evitar confusão no tmkt-api
      delete filters.data_fim
    } else if (form.value.data_ultima_compra_filtro === 'menor_que') {
      // Remove data_inicio para evitar confusão no tmkt-api
      delete filters.data_inicio
    }
  }
  
  if (form.value.nunca_comprou) {
    filters.nunca_comprou = 1
  }
  
  if (form.value.quantidade_pedidos) {
    filters.quantidade_pedidos = form.value.quantidade_pedidos
  }

  try {
    // Contar clientes usando o primeiro canal selecionado (todos retornam o mesmo)
    const tipoCampanha = canaisSelecionados.value.sms ? 'sms' : canaisSelecionados.value.push ? 'push' : 'whatsapp'
    const count = await campanhaMarketingStore.contarClientes(filters, tipoCampanha)
    if (!count || count === 0) {
      notificationStore.showNotification('Nenhum cliente elegível encontrado com os filtros informados.', 'warning')
      return
    }

    pendingFilters.value = filters
    pendingCount.value = count
    
    // Mensagem de confirmação customizada
    const mensagem = `Você está prestes a enviar uma campanha via ${canaisSelecionadosTexto.value} para ${count} cliente(s). Deseja continuar?`
    confirmationMessage.value = mensagem
    confirmDialog.value = true
  } catch (e) {
    // erro já tratado no store
  }
}

const confirmAndSend = async () => {
  confirmDialog.value = false
  if (!pendingFilters.value) return
  
  try {
    const payload = {
      ...pendingFilters.value,
      nome_campanha: form.value.nome_campanha
    }
    
    const promessas = []
    
    // Enviar para cada canal selecionado
    if (canaisSelecionados.value.push) {
      const payloadPush = {
        ...payload,
        titulo: form.value.titulo,
        mensagem: form.value.mensagem
      }
      promessas.push(campanhaMarketingStore.enviarPush(payloadPush))
    }
    
    if (canaisSelecionados.value.sms) {
      const payloadSms = {
        ...payload,
        mensagem: form.value.mensagem,
        total_clientes: pendingCount.value,
        include_nome: form.value.sms_include_nome,
        include_dtultimacompra: form.value.sms_include_dtultimacompra,
        gateway: form.value.sms_gateway
      }
      promessas.push(campanhaMarketingStore.enviarSms(payloadSms))
    }
    
    if (canaisSelecionados.value.whatsapp) {
      const payloadWhatsapp = {
        ...payload,
        type: 'template',
        content: form.value.template_code,
        include_nome: form.value.include_nome,
        include_dtultimacompra: form.value.include_dtultimacompra,
        include_valor: form.value.include_valor,
        include_revenda: form.value.include_revenda,
        include_vencimento: form.value.include_vencimento,
        provider: form.value.whatsapp_provider
      }
      promessas.push(campanhaMarketingStore.enviarWhatsapp(payloadWhatsapp))
    }
    
    // Aguardar todos os envios
    await Promise.all(promessas)
    
    notificationStore.showNotification(
      `Campanha enviada com sucesso via ${canaisSelecionadosTexto.value}!`, 
      'success'
    )
    
    limparFormulario()
  } catch (e) {
    // erro já tratado no store
  }
}

const limparFormulario = () => {
  clearBasicForm(form.value)
  form.value.nome_campanha = ''
  form.value.titulo = ''
  form.value.mensagem = ''
  form.value.sms_include_nome = false
  form.value.sms_include_dtultimacompra = false
  form.value.sms_gateway = 'smsdev'
  form.value.template_code = ''
  form.value.include_nome = false
  form.value.include_dtultimacompra = false
  form.value.include_valor = false
  form.value.include_revenda = false
  form.value.include_vencimento = false
  form.value.whatsapp_provider = 'onemsg'
  form.value.quantidade_pedidos = ''
  canaisSelecionados.value = {
    sms: false,
    push: false,
    whatsapp: false
  }
}

const clearResult = () => {
  campanhaMarketingStore.clearResult()
}

onMounted(async () => {
  // Carregar templates do WhatsApp ao montar o componente
  await campanhaMarketingStore.buscarTemplates()
})

// Carregar templates quando WhatsApp for selecionado
watch(() => canaisSelecionados.value.whatsapp, async (novoValor) => {
  if (novoValor && templates.value.length === 0) {
    await campanhaMarketingStore.buscarTemplates(form.value.whatsapp_provider)
  }
})

// Recarregar templates quando o provider de WhatsApp for alterado
watch(() => form.value.whatsapp_provider, async (novoProvider) => {
  if (canaisSelecionados.value.whatsapp) {
    form.value.template_code = ''
    await campanhaMarketingStore.buscarTemplates(novoProvider)
  }
})
</script>

<template>
  <div>
    <v-container fluid class="pt-0">
      <div class="d-flex align-center justify-space-between mb-4">
        <h1 class="text-h4">Envio de Campanha Marketing</h1>
      </div>

      <!-- Mensagem de sucesso -->
      <v-expand-transition>
        <v-card v-if="success && lastResult" variant="outlined" color="success" class="mb-6" id="operation-result">
          <v-card-text class="pa-4">
            <div class="d-flex justify-space-between align-center">
              <div class="d-flex align-center">
                <v-icon icon="mdi-check-circle" color="success" size="large" class="mr-3"></v-icon>
                <div>
                  <h3 class="text-h6 mb-1">Campanha enviada com sucesso</h3>
                  <p class="mb-0">{{ lastResult.message || 'Campanha enviada com sucesso!' }}</p>
                </div>
              </div>
              <v-btn icon="mdi-close" variant="text" density="compact" @click="clearResult"></v-btn>
            </div>
          </v-card-text>
        </v-card>
      </v-expand-transition>

      <!-- Formulário -->
      <v-card variant="outlined">
        <v-card-text class="pa-4">
          <v-form @submit.prevent="enviarCampanha">
            <!-- Seção: Seleção de Canais -->
            <div class="mb-4">
              <h3 class="text-subtitle-1 mb-3 text-primary">
                <v-icon class="mr-2" color="primary">mdi-send</v-icon>
                Canais de Envio
              </h3>
              
              <v-row>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="canaisSelecionados.sms"
                    label="SMS"
                    color="primary"
                    hide-details
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-message-text</v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="canaisSelecionados.push"
                    label="Push Notification"
                    color="primary"
                    hide-details
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-bell</v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="canaisSelecionados.whatsapp"
                    label="WhatsApp"
                    color="primary"
                    hide-details
                  >
                    <template v-slot:prepend>
                      <v-icon color="primary">mdi-whatsapp</v-icon>
                    </template>
                  </v-checkbox>
                </v-col>
              </v-row>
              
              <v-alert 
                v-if="!algumCanalSelecionado" 
                type="info" 
                variant="tonal" 
                class="mt-4"
                density="compact"
              >
                Selecione pelo menos um canal de envio acima
              </v-alert>
            </div>
            
            <v-divider class="my-6"></v-divider>
            
            <!-- Seção: Informações da Campanha -->
            <div class="mb-4">
              <h3 class="text-subtitle-1 mb-3 text-primary">
                <v-icon class="mr-2" color="primary">mdi-bullhorn</v-icon>
                Informações da Campanha
              </h3>
              
              <v-row>
                <v-col cols="12">
                  <v-text-field 
                    v-model="form.nome_campanha" 
                    label="Nome da Campanha" 
                    prepend-icon="mdi-tag" 
                    required
                    variant="outlined"
                    density="compact"
                    hide-details
                  />
                </v-col>
              </v-row>
              
              <!-- Campos específicos para Push -->
              <v-row v-if="canaisSelecionados.push" class="mt-3">
                <v-col cols="12">
                  <v-text-field 
                    v-model="form.titulo" 
                    label="Título da Notificação (Push)" 
                    prepend-icon="mdi-format-title" 
                    required
                    variant="outlined"
                    density="compact"
                    hint="Título que aparecerá na notificação push"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              
              <!-- Campos específicos para SMS e/ou Push -->
              <v-row v-if="canaisSelecionados.sms || canaisSelecionados.push" class="mt-3">
                <!-- Seletor de Gateway/API (apenas para SMS) -->
                <v-col v-if="canaisSelecionados.sms" cols="12" sm="6">
                  <v-select
                    v-model="form.sms_gateway"
                    :items="[
                      { label: 'SMSDEV', value: 'smsdev' },
                      { label: 'Twilio (em breve)', value: 'twilio', disabled: true },
                      { label: 'AWS SNS (em breve)', value: 'aws_sns', disabled: true }
                    ]"
                    item-title="label"
                    item-value="value"
                    label="Gateway/API de Envio"
                    prepend-icon="mdi-api"
                    variant="outlined"
                    density="compact"
                    hint="Selecione qual API será usada para enviar"
                    persistent-hint
                  />
                </v-col>
                
                <v-col cols="12">
                  <v-textarea 
                    v-model="form.mensagem" 
                    :label="canaisSelecionados.sms ? 'Mensagem (max. 150 caracteres para SMS)' : 'Mensagem'"
                    prepend-icon="mdi-message-text" 
                    required
                    variant="outlined"
                    rows="4"
                    :counter="canaisSelecionados.sms ? SMS_MAX_LENGTH : false"
                    :hint="canaisSelecionados.sms ? `${smsRemaining} caracteres restantes. Use {nome} e {data_ultimo_pedido} para personalizar` : 'Mensagem que será enviada'"
                    persistent-hint
                    :color="canaisSelecionados.sms && smsWarning ? 'warning' : 'primary'"
                  />
                </v-col>
                
                <!-- Variáveis dinâmicas para SMS -->
                <v-col v-if="canaisSelecionados.sms" cols="12">
                  <v-card variant="tonal" color="blue-lighten-5" class="pa-3">
                    <div class="text-subtitle-2 mb-2">
                      <v-icon size="small" class="mr-1">mdi-code-braces</v-icon>
                      Variáveis de Personalização Disponíveis
                    </div>
                    <v-row dense>
                      <v-col cols="12" sm="6">
                        <v-checkbox
                          v-model="form.sms_include_nome"
                          label="Nome do usuário - use {nome}"
                          density="compact"
                          hide-details
                          color="primary"
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-checkbox
                          v-model="form.sms_include_dtultimacompra"
                          label="Data da última compra - use {data_ultimo_pedido}"
                          density="compact"
                          hide-details
                          color="primary"
                        />
                      </v-col>
                    </v-row>
                    <v-alert v-if="form.sms_include_nome || form.sms_include_dtultimacompra" type="info" density="compact" class="mt-3">
                      <template v-if="form.sms_include_nome && form.sms_include_dtultimacompra">
                        Exemplo: "Olá {nome}, sua última compra foi em {data_ultimo_pedido}. Aproveite!"
                      </template>
                      <template v-else-if="form.sms_include_nome">
                        Exemplo: "Olá {nome}, temos uma oferta especial para você!"
                      </template>
                      <template v-else>
                        Exemplo: "Sua última compra foi em {data_ultimo_pedido}. Volte e ganhe desconto!"
                      </template>
                    </v-alert>
                  </v-card>
                </v-col>
              </v-row>
              
              <!-- Campos específicos para WhatsApp -->
              <v-row v-if="canaisSelecionados.whatsapp" class="mt-3">
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.whatsapp_provider"
                    :items="whatsappProviderOptions"
                    item-title="title"
                    item-value="value"
                    label="Provedor de WhatsApp"
                    prepend-icon="mdi-server"
                    variant="outlined"
                    density="compact"
                  >
                    <template v-slot:item="{ props, item }">
                      <v-list-item 
                        v-bind="props" 
                        :prepend-icon="item.raw.icon" 
                        :subtitle="item.raw.description"
                      />
                    </template>
                    <template v-slot:selection="{ item }">
                      <div class="d-flex align-center gap-1">
                        <v-icon :icon="item.raw.icon" size="small" />
                        <span>{{ item.raw.title }}</span>
                        <v-chip 
                          v-if="item.raw.value === 'mock'" 
                          size="x-small" 
                          color="warning"
                        >
                          TESTE
                        </v-chip>
                      </div>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.template_code"
                    :items="templates"
                    item-title="code"
                    item-value="code"
                    label="Template de Mensagem"
                    prepend-icon="mdi-file-document"
                    variant="outlined"
                    density="compact"
                    required
                    hint="Selecione um template aprovado pelo WhatsApp"
                    persistent-hint
                  >
                    <template v-slot:item="{ item, props }">
                      <v-list-item
                        v-bind="props"
                        :title="item.raw.code"
                        :subtitle="item.raw.text ? item.raw.text.substring(0, 80) + '...' : 'Sem descrição'"
                      >
                        <template v-slot:prepend>
                          <v-chip 
                            :color="item.raw.category === 'MARKETING' ? 'primary' : item.raw.category === 'UTILITY' ? 'success' : 'secondary'" 
                            size="x-small" 
                            class="mr-2 d-flex justify-center align-center"
                            style="min-width: 100px;"
                          >
                            {{ item.raw.category }}
                          </v-chip>
                        </template>
                      </v-list-item>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.include_nome"
                    label="Incluir nome do cliente"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.include_dtultimacompra"
                    label="Incluir data da última compra"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.include_valor"
                    label="Incluir valor"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.include_revenda"
                    label="Incluir revenda"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-checkbox
                    v-model="form.include_vencimento"
                    label="Incluir vencimento"
                    density="compact"
                    hide-details
                  />
                </v-col>
                
                <!-- Preview do template -->
                <v-col v-if="getTemplatePreview" cols="12">
                  <v-card variant="tonal" color="grey-lighten-3">
                    <v-card-title class="text-subtitle-1">
                      <v-icon class="mr-2">mdi-eye</v-icon>
                      Preview da Mensagem
                    </v-card-title>
                    <v-card-text>
                      <pre class="text-body-2" style="white-space: pre-wrap; font-family: inherit;">{{ getTemplatePreview }}</pre>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>
            
            <v-divider class="my-6"></v-divider>
            
            <!-- Seção: Filtros de Público -->
            <div class="mb-4">
              <h3 class="text-subtitle-1 mb-3 text-primary">
                <v-icon class="mr-2" color="primary">mdi-filter</v-icon>
                Filtros de Público
              </h3>
              
              <v-row>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="form.data_ultima_compra_filtro"
                    :items="[
                      { label: 'Entre duas datas', value: 'entre' },
                      { label: 'Maior que', value: 'maior_que' },
                      { label: 'Menor que', value: 'menor_que' },
                      { label: 'Nunca comprou (null)', value: 'null' }
                    ]"
                    item-title="label"
                    item-value="value"
                    label="Filtro de data de última compra"
                    prepend-icon="mdi-filter"
                    density="compact"
                    variant="outlined"
                    hint="Tipo de comparação"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="4" v-if="form.data_ultima_compra_filtro === 'entre' || form.data_ultima_compra_filtro === 'maior_que'">
                  <v-text-field 
                    v-model="form.data_inicio" 
                    :label="form.data_ultima_compra_filtro === 'entre' ? 'Data inicial' : 'Data (maior que)'"
                    type="date" 
                    prepend-icon="mdi-calendar"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :max="form.data_fim"
                  />
                </v-col>
                <v-col cols="12" sm="4" v-if="form.data_ultima_compra_filtro === 'entre' || form.data_ultima_compra_filtro === 'menor_que'">
                  <v-text-field 
                    v-model="form.data_fim" 
                    :label="form.data_ultima_compra_filtro === 'entre' ? 'Data final' : 'Data (menor que)'"
                    type="date" 
                    prepend-icon="mdi-calendar"
                    density="compact"
                    variant="outlined"
                    hide-details
                    :min="form.data_inicio"
                  />
                </v-col>
              </v-row>
              
              <v-row class="mt-3">
                <v-col cols="12" sm="6">
                  <v-text-field 
                    v-model="form.cidade" 
                    label="Cidade" 
                    maxlength="255" 
                    prepend-icon="mdi-city"
                    density="compact"
                    variant="outlined"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.estados"
                    :items="estadosOptions"
                    item-title="title"
                    item-value="value"
                    label="Estados"
                    multiple
                    chips
                    closable-chips
                    clearable
                    prepend-icon="mdi-map-marker"
                    density="compact"
                    variant="outlined"
                    hint="Selecione um ou mais estados"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              
              <v-row class="mt-3">
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="form.dias_vencimento_filtro"
                    :items="diasFiltroOptions"
                    item-title="label"
                    item-value="value"
                    label="Vencimento do gás"
                    clearable
                    prepend-icon="mdi-filter-variant"
                    density="compact"
                    variant="outlined"
                    hint="Tipo de comparação"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="form.dias_vencimento"
                    label="Quantidade de dias"
                    type="number"
                    min="0"
                    prepend-icon="mdi-calendar-clock"
                    density="compact"
                    variant="outlined"
                    hint="Número de dias"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field
                    v-model="form.telefone"
                    label="Telefone específico"
                    :rules="phoneRules"
                    v-mask="'(##) #####-####'"
                    prepend-icon="mdi-phone"
                    density="compact"
                    variant="outlined"
                    hint="Ex: (11) 99999-8888"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              
              <v-row class="mt-3">
                <v-col cols="12" v-if="form.data_ultima_compra_filtro !== 'null'">
                  <v-alert type="info" density="compact">
                    <template v-if="form.data_ultima_compra_filtro === 'entre'">
                      Clientes com última compra entre {{ form.data_inicio || '___' }} e {{ form.data_fim || '___' }}
                    </template>
                    <template v-else-if="form.data_ultima_compra_filtro === 'maior_que'">
                      Clientes com última compra após {{ form.data_inicio || '___' }}
                    </template>
                    <template v-else-if="form.data_ultima_compra_filtro === 'menor_que'">
                      Clientes com última compra antes de {{ form.data_fim || '___' }}
                    </template>
                  </v-alert>
                </v-col>
              </v-row>
              
              <v-row class="mt-3" v-if="false">
                <v-col cols="12">
                  <v-checkbox
                    v-model="form.nunca_comprou"
                    label="Incluir clientes que nunca compraram"
                    density="compact"
                    hide-details
                  />
                </v-col>
              </v-row>
              
              <v-row class="mt-3">
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="form.quantidade_pedidos"
                    :items="[
                      { label: 'Todos os clientes', value: '' },
                      { label: 'Exatamente 1 pedido', value: '1' },
                      { label: 'Exatamente 2 pedidos', value: '2' },
                      { label: 'Exatamente 3 pedidos', value: '3' },
                      { label: '4 ou mais pedidos', value: '4+' }
                    ]"
                    item-title="label"
                    item-value="value"
                    label="Filtrar por quantidade de pedidos"
                    clearable
                    prepend-icon="mdi-shopping-multiple"
                    density="compact"
                    variant="outlined"
                    hint="Filtrar clientes por número de compras"
                    persistent-hint
                  />
                </v-col>
              </v-row>
            </div>
            
            <v-divider class="my-6"></v-divider>
            
            <!-- Botões de ação -->
            <div class="d-flex justify-end gap-2">
              <v-btn 
                color="primary" 
                type="submit" 
                :loading="loading"
                size="large"
              >
                <v-icon class="mr-2">mdi-send</v-icon>
                Enviar Campanha
              </v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Dialog de confirmação -->
    <v-dialog v-model="confirmDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">
          <v-icon class="mr-2" color="warning">mdi-alert-circle</v-icon>
          Confirmar Envio
        </v-card-title>
        <v-card-text>
          <p class="text-body-1">{{ confirmationMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="confirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" variant="flat" @click="confirmAndSend">Confirmar e Enviar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.gap-2 {
  gap: 0.5rem;
}
</style>
